/**
 * Query keys for finance-related React Query cache
 */
import type { TransactionFilters } from '@/types/domain/finance';

export interface ExpenseListParams {
  search?: string;
  cursor?: string | null;
  limit?: number;
  filters?: string;
  [key: string]: unknown;
}

export interface UnifiedTransactionsParams {
  page?: number;
  limit?: number;
  filters?: TransactionFilters;
}

export interface FinancePaymentsPageParams {
  page?: number;
  cursor?: string | null;
  limit?: number;
  filters?: TransactionFilters;
}

export interface PayoutsFilters {
  status?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
  skip?: number;
}

/** Stable serialization for object query keys */
function stableKey(obj: Record<string, unknown> | undefined): string {
  if (!obj || Object.keys(obj).length === 0) return '';
  return JSON.stringify(obj);
}

export const financeQueryKeys = {
  all: ['finance'] as const,
  metricsSummaryAll: () => [...financeQueryKeys.all, 'metricsSummary'] as const,
  metricsSummary: (days: number) => [...financeQueryKeys.metricsSummaryAll(), days] as const,
  metricsSummaryForBusiness: (days: number, businessId: string) =>
    [...financeQueryKeys.metricsSummary(days), businessId] as const,
  expensesLists: () => [...financeQueryKeys.all, 'expenses', 'list'] as const,
  expensesList: (params: ExpenseListParams) =>
    [...financeQueryKeys.expensesLists(), stableKey(params as Record<string, unknown>)] as const,
  expenseDetails: () => [...financeQueryKeys.all, 'expenses', 'detail'] as const,
  expenseDetail: (id: string) => [...financeQueryKeys.expenseDetails(), id] as const,
  unifiedTransactionsAll: () => [...financeQueryKeys.all, 'unifiedTransactions'] as const,
  unifiedTransactions: (params: UnifiedTransactionsParams) =>
    [
      ...financeQueryKeys.unifiedTransactionsAll(),
      stableKey(params as Record<string, unknown>),
    ] as const,
  unifiedTransactionsForBusiness: (params: UnifiedTransactionsParams, businessId: string) =>
    [...financeQueryKeys.unifiedTransactions(params), businessId] as const,
  financePaymentsPagesAll: () => [...financeQueryKeys.all, 'financePaymentsPage'] as const,
  financePaymentsPage: (params: FinancePaymentsPageParams) =>
    [
      ...financeQueryKeys.financePaymentsPagesAll(),
      stableKey(params as Record<string, unknown>),
    ] as const,
  financePaymentsPageForBusiness: (params: FinancePaymentsPageParams, businessId: string) =>
    [...financeQueryKeys.financePaymentsPage(params), businessId] as const,
  balance: () => [...financeQueryKeys.all, 'balance'] as const,
  payoutsAll: () => [...financeQueryKeys.all, 'payouts'] as const,
  payouts: (filters?: PayoutsFilters) =>
    [
      ...financeQueryKeys.payoutsAll(),
      stableKey(filters as Record<string, unknown>) ?? '',
    ] as const,
  jobPayoutsCard: (businessId: string) =>
    [...financeQueryKeys.all, 'jobPayoutsCard', businessId] as const,
  stripeAccount: (businessId: string) =>
    [...financeQueryKeys.all, 'stripeAccount', businessId] as const,
  stripeConnectStatus: (businessId: string) =>
    [...financeQueryKeys.all, 'stripeConnectStatus', businessId] as const,
  invoiceRefundActivity: (invoiceId: string) =>
    [...financeQueryKeys.all, 'invoices', invoiceId, 'refund-activity'] as const,
  invoiceRefundActivityDisabled: () =>
    [...financeQueryKeys.all, 'invoices', 'refund-activity', 'disabled'] as const,
  transactionDetail: (transactionId: string) => ['transaction-detail', transactionId] as const,
  savedCards: () => [...financeQueryKeys.all, 'saved-cards'] as const,
  stripeTaxCodes: () => [...financeQueryKeys.all, 'stripe-tax-codes'] as const,
  automaticTaxStatus: () => [...financeQueryKeys.all, 'automatic-tax', 'status'] as const,
  automaticTaxStatusForBusiness: (businessId: string) =>
    [...financeQueryKeys.automaticTaxStatus(), businessId] as const,
  businessFinanceSettings: (businessId: string) =>
    [...financeQueryKeys.all, 'businessFinanceSettings', businessId] as const,
  projectProfit: (projectId: string) =>
    [...financeQueryKeys.all, 'projectProfit', projectId] as const,
  projectPendingExpenses: (projectId: string) =>
    [...financeQueryKeys.all, 'projectPendingExpenses', projectId] as const,
  memberPayRate: (businessId: string, userId: string) =>
    [...financeQueryKeys.all, 'memberPayRate', businessId, userId] as const,
};
